import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Users, 
  FileText, 
  BarChart3, 
  Download, 
  Plus, 
  Copy, 
  ExternalLink,
  Loader2,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  Building2,
  Droplets,
  Leaf,
  Shield,
  AlertTriangle,
  Target,
  Database,
  GitBranch
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { FullSupplyChainMap } from "@/components/SupplyChainMap";
import { 
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend,
  ScatterChart, Scatter, ZAxis, LineChart, Line
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_4aa3e30f-645a-43e8-83ed-b83576a14c9f/artifacts/p43xkrnl_SusTek%20Colored%20Logo%20Name%20-%20Horizontal.svg";

const AdminDashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [heatmapData, setHeatmapData] = useState([]);
  const [invites, setInvites] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [isCreatingInvite, setIsCreatingInvite] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const [newInvite, setNewInvite] = useState({ email: "", company_name: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [supplyChainData, setSupplyChainData] = useState(null);
  const [isLoadingSupplyChain, setIsLoadingSupplyChain] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [suppliersRes, analyticsRes, invitesRes, heatmapRes] = await Promise.all([
        axios.get(`${API}/admin/suppliers`),
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/invites`),
        axios.get(`${API}/admin/heatmap`)
      ]);
      setSuppliers(suppliersRes.data);
      setAnalytics(analyticsRes.data);
      setInvites(invitesRes.data);
      setHeatmapData(heatmapRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  };

  const loadSupplyChain = async () => {
    if (supplyChainData) return;
    setIsLoadingSupplyChain(true);
    try {
      const res = await axios.get(`${API}/admin/supply-chain-map`);
      setSupplyChainData(res.data);
    } catch {
      toast.error("Failed to load supply chain data");
    } finally {
      setIsLoadingSupplyChain(false);
    }
  };

  const createInvite = async () => {
    setIsCreatingInvite(true);
    try {
      const response = await axios.post(`${API}/invites`, newInvite);
      setInvites([response.data, ...invites]);
      toast.success("Invite created successfully");
      setNewInvite({ email: "", company_name: "" });
      setDialogOpen(false);
    } catch (error) {
      toast.error("Failed to create invite");
    } finally {
      setIsCreatingInvite(false);
    }
  };

  const seedData = async () => {
    setIsSeeding(true);
    try {
      await axios.post(`${API}/admin/seed-data`);
      toast.success("Sample data created successfully");
      loadData();
    } catch (error) {
      toast.error("Failed to seed data");
    } finally {
      setIsSeeding(false);
    }
  };

  const copyInviteLink = (token) => {
    const link = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied to clipboard");
  };

  const exportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'esg_questionnaire_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV exported successfully");
    } catch (error) {
      toast.error("Failed to export CSV. No completed questionnaires found.");
    }
  };

  const getStatusBadge = (status) => {
    if (status?.is_complete) {
      return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Completed</Badge>;
    } else if (status?.current_section > 0) {
      return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">In Progress</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">Not Started</Badge>;
  };

  const getMaturityBadge = (tier) => {
    const colors = {
      "Leading": "bg-emerald-100 text-emerald-800",
      "Mature": "bg-blue-100 text-blue-800",
      "Developing": "bg-amber-100 text-amber-800",
      "High Attention Needed": "bg-orange-100 text-orange-800",
      "Critical Risk": "bg-red-100 text-red-800",
      "Not Assessed": "bg-gray-100 text-gray-800"
    };
    return <Badge className={`${colors[tier] || colors["Not Assessed"]} hover:${colors[tier]}`}>{tier}</Badge>;
  };

  const COLORS = ['#1A4D2E', '#4F6F52', '#7DB9B6', '#E8DFCA', '#F97316', '#EF4444'];
  const MATURITY_COLORS = {
    "Leading": "#10B981",
    "Mature": "#3B82F6", 
    "Developing": "#F59E0B",
    "High Attention Needed": "#F97316",
    "Critical Risk": "#EF4444"
  };

  // Prepare chart data
  const maturityChartData = analytics?.maturity_distribution ? 
    Object.entries(analytics.maturity_distribution).map(([name, value]) => ({ 
      name, 
      value,
      fill: MATURITY_COLORS[name] || "#9CA3AF"
    })) : [];

  const waterStressData = analytics?.water_stress_distribution ?
    Object.entries(analytics.water_stress_distribution).map(([name, value]) => ({ name, value })) : [];

  const sbtiChartData = analytics?.sbti_distribution ? 
    Object.entries(analytics.sbti_distribution).map(([name, value]) => ({ name, value })) : [];

  const certChartData = analytics?.certification_distribution ?
    Object.entries(analytics.certification_distribution)
      .filter(([name]) => name !== "None")
      .slice(0, 6)
      .map(([name, value]) => ({ name, value })) : [];

  const riskTypeData = analytics?.risk_type_distribution ?
    Object.entries(analytics.risk_type_distribution).map(([name, value]) => ({ name, value })) : [];

  const radarData = analytics?.average_scores ? [
    { subject: 'Environmental', score: analytics.average_scores.environmental || 0, fullMark: 100 },
    { subject: 'Social', score: analytics.average_scores.social || 0, fullMark: 100 },
    { subject: 'Governance', score: analytics.average_scores.governance || 0, fullMark: 100 },
    { subject: 'Resilience', score: analytics.average_scores.resilience || 0, fullMark: 100 },
  ] : [];

  // Risk heat map quadrant data
  const quadrantData = heatmapData.map(s => ({
    ...s,
    x: s.overall_score || 0,
    y: s.criticality === "Critical" ? 100 : s.criticality === "High" ? 75 : s.criticality === "Medium" ? 50 : 25,
    z: Math.log(s.annual_spend + 1) * 5
  }));

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cream to-white flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-forest animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50" data-testid="admin-header">
        <div className="container-main py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="admin-logo">
            <img src={LOGO_HORIZONTAL} alt="SusTek" className="h-10" />
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">Admin Portal</span>
            <Link to="/">
              <Button variant="outline" size="sm" data-testid="admin-home-btn">
                <ExternalLink className="w-4 h-4 mr-2" />
                View Site
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container-main py-8">
        {/* Page Title */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">GreenCircuit Insight</h1>
            <p className="text-muted-foreground">AI-Infused Sustainable Supply Chain Analytics</p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={seedData}
              disabled={isSeeding}
              data-testid="admin-seed-btn"
            >
              {isSeeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
              Seed Sample Data
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="admin-create-invite-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Invite
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Supplier Invite</DialogTitle>
                  <DialogDescription>
                    Generate a unique invite link for a supplier to complete the ESG questionnaire.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="invite-email">Email (Optional)</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="supplier@company.com"
                      value={newInvite.email}
                      onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })}
                      data-testid="invite-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invite-company">Company Name (Optional)</Label>
                    <Input
                      id="invite-company"
                      type="text"
                      placeholder="Supplier Company Ltd."
                      value={newInvite.company_name}
                      onChange={(e) => setNewInvite({ ...newInvite, company_name: e.target.value })}
                      data-testid="invite-company-input"
                    />
                  </div>
                  <Button 
                    onClick={createInvite} 
                    className="w-full btn-primary"
                    disabled={isCreatingInvite}
                    data-testid="invite-create-btn"
                  >
                    {isCreatingInvite ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    Generate Invite Link
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button variant="outline" onClick={exportCSV} data-testid="admin-export-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-border p-1 rounded-lg">
            <TabsTrigger value="overview" className="data-[state=active]:bg-forest data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-2" />Overview
            </TabsTrigger>
            <TabsTrigger value="scores" className="data-[state=active]:bg-forest data-[state=active]:text-white">
              <Target className="w-4 h-4 mr-2" />ESG Scores
            </TabsTrigger>
            <TabsTrigger value="heatmap" className="data-[state=active]:bg-forest data-[state=active]:text-white">
              <AlertTriangle className="w-4 h-4 mr-2" />Risk Heat Map
            </TabsTrigger>
            <TabsTrigger value="climate" className="data-[state=active]:bg-forest data-[state=active]:text-white">
              <Leaf className="w-4 h-4 mr-2" />Climate & Water
            </TabsTrigger>
            <TabsTrigger value="supplychain" className="data-[state=active]:bg-forest data-[state=active]:text-white" onClick={loadSupplyChain}>
              <GitBranch className="w-4 h-4 mr-2" />Supply Chain
            </TabsTrigger>
            <TabsTrigger value="suppliers" className="data-[state=active]:bg-forest data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />Suppliers
            </TabsTrigger>
            <TabsTrigger value="invites" className="data-[state=active]:bg-forest data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />Invites
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="border-border/50" data-testid="stat-suppliers">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Suppliers</p>
                      <p className="text-3xl font-bold text-foreground">{analytics?.total_suppliers || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-forest" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50" data-testid="stat-completed">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Completed Assessments</p>
                      <p className="text-3xl font-bold text-foreground">{analytics?.completed_questionnaires || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50" data-testid="stat-score">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Average ESG Score</p>
                      <p className="text-3xl font-bold text-foreground">
                        {analytics?.average_scores?.overall?.toFixed(1) || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-sage/20 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-sage" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50" data-testid="stat-flags">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Critical Flags</p>
                      <p className="text-3xl font-bold text-foreground">
                        {analytics?.flags_distribution?.critical || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Maturity Distribution */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Supplier Maturity Distribution</CardTitle>
                  <CardDescription>ESG maturity tiers across supplier base</CardDescription>
                </CardHeader>
                <CardContent>
                  {maturityChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={maturityChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={90}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {maturityChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                      No data available yet
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* ESG Score Radar */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Average ESG Pillar Scores</CardTitle>
                  <CardDescription>Portfolio performance across ESG dimensions</CardDescription>
                </CardHeader>
                <CardContent>
                  {radarData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <RadarChart data={radarData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} />
                        <Radar name="Score" dataKey="score" stroke="#1A4D2E" fill="#1A4D2E" fillOpacity={0.5} />
                        <Tooltip />
                      </RadarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                      No data available yet
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Risk Types */}
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading text-lg">AI Risk Flags by Category</CardTitle>
                <CardDescription>Automated risk detection across supplier portfolio</CardDescription>
              </CardHeader>
              <CardContent>
                {riskTypeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={riskTypeData} layout="vertical">
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#1A4D2E" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    No risk flags detected yet
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ESG Scores Tab */}
          <TabsContent value="scores" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Score Cards */}
              <div className="space-y-4">
                <Card className="border-border/50 bg-gradient-to-r from-emerald-50 to-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Environmental Score</p>
                        <p className="text-4xl font-bold text-emerald-700">
                          {analytics?.average_scores?.environmental?.toFixed(1) || 0}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">Climate, Water, Waste</p>
                      </div>
                      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                        <Leaf className="w-8 h-8 text-emerald-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border/50 bg-gradient-to-r from-blue-50 to-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Social Score</p>
                        <p className="text-4xl font-bold text-blue-700">
                          {analytics?.average_scores?.social?.toFixed(1) || 0}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">Labor, H&S, Human Rights</p>
                      </div>
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                        <Users className="w-8 h-8 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border/50 bg-gradient-to-r from-purple-50 to-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Governance Score</p>
                        <p className="text-4xl font-bold text-purple-700">
                          {analytics?.average_scores?.governance?.toFixed(1) || 0}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">Ethics, Cyber, Compliance</p>
                      </div>
                      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                        <Shield className="w-8 h-8 text-purple-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border/50 bg-gradient-to-r from-amber-50 to-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Resilience Score</p>
                        <p className="text-4xl font-bold text-amber-700">
                          {analytics?.average_scores?.resilience?.toFixed(1) || 0}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">Certifications, Risk Exposure</p>
                      </div>
                      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                        <Target className="w-8 h-8 text-amber-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Certifications Chart */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Top Certifications</CardTitle>
                  <CardDescription>Most common ESG certifications held by suppliers</CardDescription>
                </CardHeader>
                <CardContent>
                  {certChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={certChartData} layout="vertical">
                        <XAxis type="number" />
                        <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#4F6F52" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                      No certification data available yet
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Risk Heat Map Tab */}
          <TabsContent value="heatmap" className="space-y-6">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Supplier Risk Heat Map</CardTitle>
                <CardDescription>
                  ESG Score vs. Business Criticality - bubble size indicates annual spend
                </CardDescription>
              </CardHeader>
              <CardContent>
                {quadrantData.length > 0 ? (
                  <div className="relative">
                    <ResponsiveContainer width="100%" height={500}>
                      <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                        <XAxis 
                          type="number" 
                          dataKey="x" 
                          name="ESG Score" 
                          domain={[0, 100]}
                          label={{ value: 'ESG Score →', position: 'bottom', offset: 40 }}
                        />
                        <YAxis 
                          type="number" 
                          dataKey="y" 
                          name="Criticality" 
                          domain={[0, 100]}
                          label={{ value: '← Business Criticality', angle: -90, position: 'left', offset: 40 }}
                          tickFormatter={(v) => v === 100 ? 'Critical' : v === 75 ? 'High' : v === 50 ? 'Medium' : 'Low'}
                        />
                        <ZAxis type="number" dataKey="z" range={[50, 400]} />
                        <Tooltip 
                          content={({ payload }) => {
                            if (payload && payload[0]) {
                              const data = payload[0].payload;
                              return (
                                <div className="bg-white p-3 rounded-lg shadow-lg border">
                                  <p className="font-semibold">{data.company_name}</p>
                                  <p className="text-sm">ESG Score: {data.overall_score?.toFixed(1)}</p>
                                  <p className="text-sm">Criticality: {data.criticality}</p>
                                  <p className="text-sm">Spend: ${(data.annual_spend || 0).toLocaleString()}</p>
                                  <p className="text-sm">Maturity: {data.maturity_tier}</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Scatter 
                          data={quadrantData} 
                          fill="#1A4D2E"
                          fillOpacity={0.6}
                        />
                        {/* Quadrant lines */}
                        <line x1="50%" y1="0" x2="50%" y2="100%" stroke="#E5E7EB" strokeDasharray="5 5" />
                        <line x1="0" y1="50%" x2="100%" y2="50%" stroke="#E5E7EB" strokeDasharray="5 5" />
                      </ScatterChart>
                    </ResponsiveContainer>
                    {/* Quadrant Labels */}
                    <div className="absolute top-8 left-20 text-xs text-red-600 font-medium bg-red-50 px-2 py-1 rounded">
                      ⚠️ Immediate Action
                    </div>
                    <div className="absolute top-8 right-8 text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded">
                      ✓ Strategic Partners
                    </div>
                    <div className="absolute bottom-20 left-20 text-xs text-orange-600 font-medium bg-orange-50 px-2 py-1 rounded">
                      Replace or Improve
                    </div>
                    <div className="absolute bottom-20 right-8 text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
                      Monitor
                    </div>
                  </div>
                ) : (
                  <div className="h-[500px] flex items-center justify-center text-muted-foreground">
                    No supplier data available for heat map
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Critical Suppliers Table */}
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading text-lg">High-Risk Suppliers Requiring Attention</CardTitle>
                <CardDescription>Critical and high criticality suppliers with low ESG scores</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-cream">
                      <TableHead>Supplier</TableHead>
                      <TableHead>Criticality</TableHead>
                      <TableHead>ESG Score</TableHead>
                      <TableHead>Maturity</TableHead>
                      <TableHead>Flags</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {heatmapData
                      .filter(s => (s.criticality === "Critical" || s.criticality === "High") && s.overall_score < 60)
                      .slice(0, 10)
                      .map((supplier) => (
                        <TableRow key={supplier.supplier_id} className="hover:bg-cream/50">
                          <TableCell className="font-medium">{supplier.company_name}</TableCell>
                          <TableCell>
                            <Badge className={supplier.criticality === "Critical" ? "bg-red-100 text-red-800" : "bg-orange-100 text-orange-800"}>
                              {supplier.criticality}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className={supplier.overall_score < 40 ? "text-red-600 font-bold" : "text-orange-600 font-medium"}>
                              {supplier.overall_score?.toFixed(1)}
                            </span>
                          </TableCell>
                          <TableCell>{getMaturityBadge(supplier.maturity_tier)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="border-red-300 text-red-700">
                              {supplier.flags_count} flags
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Link to={`/admin/supplier/${supplier.supplier_id}`}>
                              <Button variant="ghost" size="sm">View Details</Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Climate & Water Tab */}
          <TabsContent value="climate" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* SBTi Status */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Science-Based Target Status</CardTitle>
                  <CardDescription>Distribution of supplier SBTi commitments</CardDescription>
                </CardHeader>
                <CardContent>
                  {sbtiChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={sbtiChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {sbtiChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      No SBTi data available yet
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Water Stress */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Droplets className="w-5 h-5 text-blue-500" />
                    Water Stress Exposure
                  </CardTitle>
                  <CardDescription>Suppliers by operating region water stress level</CardDescription>
                </CardHeader>
                <CardContent>
                  {waterStressData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={waterStressData}>
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      No water stress data available yet
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Emissions Stats */}
            <Card className="border-border/50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-forest/10 rounded-2xl flex items-center justify-center">
                    <TrendingUp className="w-10 h-10 text-forest" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Average Supplier Scope 1+2 Emissions</p>
                    <p className="text-3xl font-bold text-foreground">
                      {analytics?.avg_emissions?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || 0} tCO₂e
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Based on {analytics?.completed_questionnaires || 0} completed assessments
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Supply Chain Map Tab */}
          <TabsContent value="supplychain" className="space-y-6" data-testid="tab-supplychain">
            <FullSupplyChainMap data={supplyChainData} isLoading={isLoadingSupplyChain} />
          </TabsContent>

          {/* Suppliers Tab */}
          <TabsContent value="suppliers" className="space-y-6">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading text-lg">All Suppliers</CardTitle>
                <CardDescription>Complete supplier registry with ESG assessment status</CardDescription>
              </CardHeader>
              <CardContent>
                {suppliers.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-cream">
                        <TableHead>Company</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Criticality</TableHead>
                        <TableHead>ESG Score</TableHead>
                        <TableHead>Maturity</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {suppliers.slice(0, 20).map((supplier) => (
                        <TableRow key={supplier.id} className="hover:bg-cream/50">
                          <TableCell className="font-medium">{supplier.company_name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{supplier.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{supplier.criticality_level || "Medium"}</Badge>
                          </TableCell>
                          <TableCell>
                            {supplier.questionnaire_status?.esg_scores?.overall_score?.toFixed(1) || "-"}
                          </TableCell>
                          <TableCell>
                            {getMaturityBadge(supplier.questionnaire_status?.esg_scores?.maturity_tier || "Not Assessed")}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(supplier.questionnaire_status)}
                          </TableCell>
                          <TableCell>
                            <Link to={`/admin/supplier/${supplier.id}`}>
                              <Button variant="ghost" size="sm" data-testid={`view-supplier-${supplier.id}`}>
                                View
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No suppliers registered yet</p>
                  </div>
                )}
                {suppliers.length > 20 && (
                  <p className="text-sm text-muted-foreground text-center mt-4">
                    Showing 20 of {suppliers.length} suppliers
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invites Tab */}
          <TabsContent value="invites" className="space-y-6">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Invite Links</CardTitle>
                <CardDescription>Manage supplier invitation links</CardDescription>
              </CardHeader>
              <CardContent>
                {invites.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-cream">
                        <TableHead>Company</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invites.map((invite) => (
                        <TableRow key={invite.id} className="hover:bg-cream/50">
                          <TableCell className="font-medium">
                            {invite.company_name || "-"}
                          </TableCell>
                          <TableCell>{invite.email || "-"}</TableCell>
                          <TableCell>
                            {new Date(invite.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {invite.is_used ? (
                              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Used</Badge>
                            ) : (
                              <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Active</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => copyInviteLink(invite.token)}
                              disabled={invite.is_used}
                              data-testid={`copy-invite-${invite.id}`}
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Copy Link
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No invites created yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
